import React from "react";
import * as BS from "react-bootstrap";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";
import { push } from "react-router-redux";
import * as RS from "react-sortable-hoc";
import * as redux from "redux";

import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import { AppState } from "../../../store";
import { Paths } from "../../../constants";
import Settings from "../../../settings";
import { NavigationTypes } from "../../../domain";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    api: new Api(state),
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    dispatchUrl: (url: string) => dispatch(push(url)),
    expectNavigationToReload: () => dispatch(Actions.Nav.startExpectedRefetchTimer()),
    notifyError: (message: string, icon?: string) => dispatch(Actions.Noti.notifyError(message, icon)),
    notifySuccess: (message: string, icon?: string) => dispatch(Actions.Noti.notifySuccess(message, icon)),
});

interface RouteParams {
    itemId?: string;
}

interface OwnProps extends RouteComponentProps<RouteParams> {
}

interface ConnectedState {
    api: Api;
}

interface ConnectedDispatch {
    dispatchUrl: (url: string) => void;
    expectNavigationToReload: () => void;
    notifyError: (message: string, icon?: string) => void;
    notifySuccess: (message: string, icon?: string) => void;
}

interface OwnState {
    navItem?: M.AdminNav.NavListItem;
    children: M.AdminNav.NavChildItem[];
}

class SortItemsComponent extends Comp.Base.BaseComponent<ConnectedState & ConnectedDispatch & OwnProps, OwnState> {

    public constructor(props: any) {
        super(props);

        this.state = {
            loading: true,
            children: [],
        };
    }

    public componentDidMount() {
        this._isMounted = true;

        const itemId = parseInt(this.props.match.params.itemId || "", 10);
        if (isNaN(itemId)) {
            // invalid item id
            this.errorAndReturn(`Item id ${this.props.match.params.itemId} is invalid`);
        }
        else {
            // setup promises and load nav item and immediate children
            const qpNavItem = { queryParams: { id: itemId, appId: Settings.AppID }};
            const qpChildren = { queryParams: { id: itemId, appId: Settings.AppID, onMenu: true }};
            const promiseNavItem = this.props.api.get<M.AdminNav.NavListItem>(Api.endpoints.admin.nav.item, qpNavItem);
            const promiseChildren = this.props.api.get<M.AdminNav.NavListItem[]>(Api.endpoints.admin.nav.children, qpChildren);

            Promise.all([promiseNavItem, promiseChildren]).then((result) => {
                if (this._isMounted) {
                    const navItemResult = result[0];
                    const childrenResult = result[1];

                    if (navItemResult.isSuccess && childrenResult.isSuccess && navItemResult.data) {
                        if (!NavigationTypes.isContentType(navItemResult.data.typeId)) {
                            this.errorAndReturn(`Navigation item <strong>${navItemResult.data.title}</strong> is not allowed for sorting child items.`);
                            return;
                        }

                        if (!(childrenResult.data || []).length) {
                            // no children, so return to list view
                            this.errorAndReturn(`Navigation item <strong>${navItemResult.data.title}</strong> currently has no child items to sort.`);
                            return;
                        }

                        this.setState({
                            ...this.state,
                            navItem: navItemResult.data,
                            children: childrenResult.data || [],
                        });
                    }
                    else {
                        throw new Error (navItemResult.errors.concat(childrenResult.errors).join("<br />"));
                    }
                }
            }, (err) => {
                // error
                throw new Error(err);
            })
            .catch((err) => {
                if (this._isMounted) {
                    // caught error
                    this.errorAndReturn(err.message || err);
                }
            })
            .then(() => {
                if (this._isMounted) {
                    // done loading
                    this.toggleLoading(false);
                }
            });
        }
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        const breadcrumb = [
            <Link to={Paths.Admin.Landing}>Admin</Link>,
            <Link to={Paths.AdminNav.List}>Navigation</Link>,
        ];

        const SortableItem = RS.SortableElement((c: M.AdminNav.NavChildItem) => {
            const itemTitle = c.typeId === NavigationTypes.Separator.id
                ? <em>&ndash;Separator&ndash;</em>
                : c.title;
            return <li className="list-group-item"><i className="fa fa-ellipsis-v"></i> {itemTitle} &nbsp;</li>;
        });

        const SortableList = RS.SortableContainer((items: { children: M.AdminNav.NavChildItem[] }) => {
            return <ul className="list-group">
                    {items.children.map((c, i) => <SortableItem {...c} key={i} index={i} />)}
                </ul>;
        });

        const buttons = this.state.children.length
            ? <div>
                <BS.Button bsStyle="link" onClick={() => this.onCancelClick()}>Cancel</BS.Button>
                <BS.Button bsStyle="primary" onClick={() => this.onSaveClick()}>Save</BS.Button>
            </div>
            : null;

        const navItem = this.state.navItem;

        return <Comp.Page.AdminPage breadcrumb={breadcrumb} title={"Sort navigation items"}>
            <div style={{ position: "relative" }}>
                <Comp.Generic.LoadingSpinner show={this.state.loading} />
                <h2>{navItem ? (navItem.fullTitle + (navItem.parentIds.length > 1 ? navItem.title : "")) : ""}</h2>

                <p>Drag and drop individual navigation items and press Save to update the sequence.</p>

                <SortableList children={this.state.children} onSortEnd={(sort, evt) => this.onSortEnd(sort, evt)} />

                {buttons}
            </div>
        </Comp.Page.AdminPage>;
    }

    /**
     * Displays an error notification and returns to the admin nav list view
     * @param err The error message
     */
    private errorAndReturn(err: string) {
        this.props.notifyError(err);
        this.props.dispatchUrl(Paths.AdminNav.List);
    }

    /**
     * Handle cancle click
     */
    private onCancelClick() {
        this.successAndReturn();
    }

    /**
     * Handle save click
     */
    private async onSaveClick() {
        this.toggleLoading(true);
        const result = await this.props.api.put<M.AdminNav.NavChildItem[]>(Api.endpoints.admin.nav.updateSort, {
            body: JSON.stringify({
                appId: Settings.AppID,
                parentId: (this.state.navItem || { id: 0 }).id,
                children: this.state.children.map((c, i) => ({ id: c.id, sort: i })),
            }),
        });

        if (this._isMounted) {
            const navItem = this.state.navItem;
            if (result.isSuccess && result.data)
                this.successAndReturn(`Sequence of child items for <strong>${(navItem ? navItem.title : "")}</strong> has been successfully updated.`);
            else
                this.errorAndReturn(result.errors.join("<br />"));

            this.toggleLoading(false);
        }

        this.props.expectNavigationToReload();
    }

    /**
     * Re-sorts the children of the state based on sort event
     * @param sort
     * @param event
     */
    private onSortEnd(sort: RS.SortEnd, event: RS.SortEvent) {
        this.setState({
            ...this.state,
            children: RS.arrayMove(this.state.children, sort.oldIndex, sort.newIndex),
        });
    }

    /**
     * Optionally displays a success message and returns to the admin nav list view
     * @param msg Optional success message
     */
    private successAndReturn(msg?: string) {
        if (msg)
            this.props.notifySuccess(msg);
        this.props.dispatchUrl(Paths.AdminNav.List);
    }
}

export const SortItems: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(SortItemsComponent);



// WEBPACK FOOTER //
// ./src/containers/adminNav/sortItems/SortItems.tsx